import { currencyFormatter } from '../../pages/QuotePage/content/QuoteGrids/utils';

/* The reporting API's answer to GET /quotes/{id}/tax-basis and GET /policies/{id}/mces/{id}/tax-basis.
   Its Gson omits null fields, so `commission` (non-commission states) and `asOf` (no effective date)
   can be missing rather than null; both are read with `== null`. */
export type TaxBasisRuleName = 'commission_in_basis' | 'round_basis_to_dollar' | 'round_taxes_to_dollar';

export interface TaxBasisRule {
  rule: string;
  notes?: string | null;
}

export interface TaxBasisNote {
  state: string;
  asOf?: string | null;
  premium: number;
  commission?: number | null;
  basis: number;
  rules?: TaxBasisRule[] | null;
}

/* What a fee grid hands over for one of its rows. `rate` is a percent (4 means 4%); `amount` is the
   fee's stored amount, which is what the grid itself displays. */
export interface TaxBasisFeeRow {
  name?: string | null;
  rate?: number | null;
  amount?: number | null;
  isMunicipal?: boolean | null;
}

export interface TaxBasisTooltip {
  headlines: string[];
  calculation: string | null;
  citations: string[];
}

const KNOWN_RULES: ReadonlySet<string> = new Set<TaxBasisRuleName>(['commission_in_basis', 'round_basis_to_dollar', 'round_taxes_to_dollar']);

const CITATION_MAX_LENGTH = 180;

export const STATE_NAMES: Readonly<Record<string, string>> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
};

export function stateName(code: string | null | undefined): string {
  const key = (code ?? '').trim().toUpperCase();
  return STATE_NAMES[key] ?? key;
}

/* A tax row is a rate-based fee that is not the brokerage commission, not collateral, and not a
   municipal tax. A flat-amount fee (no rate) is left out: it is not levied on the basis, so a note
   about how the basis is built would be misleading next to it. */
export function isTaxRow(row: TaxBasisFeeRow | null | undefined): boolean {
  if (!row) return false;
  const name = String(row.name ?? '').toLowerCase();
  if (!name || /broker/.test(name) || /collateral/.test(name)) return false;
  if (row.isMunicipal === true) return false;
  const rate = Number(row.rate);
  return Number.isFinite(rate) && rate > 0;
}

const money = (value: number | null | undefined): string =>
  // currencyFormatter renders "$ 1,234.56"; the space reads oddly inside a sentence.
  currencyFormatter({ field: { value: Number(value) || 0 }, withDecimals: true, upto: 2 }).replace(/^\$\s+/, '$');

const percent = (rate: number): string => `${Number(rate.toFixed(4))}%`;

const truncate = (text: string, max: number): string => (text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text);

/* The tooltip for one fee row, or null when there is nothing to say: no note, no rule in force the
   UI knows about, or the row is not a tax. */
export function buildTaxBasisTooltip(note: TaxBasisNote | null | undefined, row: TaxBasisFeeRow | null | undefined): TaxBasisTooltip | null {
  if (!note || !isTaxRow(row)) return null;
  const rules = (note.rules ?? []).filter((r) => r && KNOWN_RULES.has(r.rule));
  if (!rules.length) return null;

  const has = (rule: TaxBasisRuleName) => rules.some((r) => r.rule === rule);
  const commissionInBasis = has('commission_in_basis');
  const roundBasis = has('round_basis_to_dollar');
  const roundTaxes = has('round_taxes_to_dollar');
  const state = stateName(note.state);

  const headlines: string[] = [];
  if (commissionInBasis) headlines.push(`${state} taxes premium + brokerage commission.`);
  if (roundBasis)
    headlines.push(commissionInBasis ? `${state} rounds the taxable basis to the dollar.` : `${state} taxes the premium rounded to the dollar.`);
  if (roundTaxes) headlines.push(`${state} rounds each tax to the whole dollar.`);

  const rate = Number(row?.rate);
  const amount = money(row?.amount);
  const operator = roundTaxes ? '→' : '=';
  const calculation =
    commissionInBasis && note.commission != null
      ? `${percent(rate)} × (${money(note.premium)} premium + ${money(note.commission)} commission) ${operator} ${amount}`
      : `${percent(rate)} × ${money(note.basis)} ${operator} ${amount}`;

  const citations = rules
    .map((r) => (r.notes ?? '').trim())
    .filter((text, i, all) => text && all.indexOf(text) === i)
    .map((text) => truncate(text, CITATION_MAX_LENGTH));

  return { headlines, calculation, citations };
}
